import Dayjs from 'dayjs'

// CSV import + template download on the centre pages (Petty Cash, Consumables,
// Program Material) is hidden for now — the feature is kept and fully wired, we
// just don't expose it to the centre teams yet. Flip this to `true` to relaunch
// it everywhere at once. (HQ Budgets keeps its own CSV import, unaffected.)
export const SHOW_CSV_IMPORT = false

// Default month for the centre ordering pages (Program Material + Consumables):
// from the 15th of a month onward, default to NEXT month so centre teams plan
// next month's ordering ahead of time (e.g. 15 Jul onward defaults to August).
// Before the 15th it stays on the current month. Only the *default* — the month
// picker still lets them choose any month, and an explicit initialMonth wins.
export const defaultOrderMonth = () => {
  const now = Dayjs()
  return (now.date() >= 15 ? now.add(1, 'month') : now).format('YYYY-MM')
}

// Budget categories — values MUST match the oh-api ProcurementCategory enum.
export const PROCUREMENT_CATEGORIES = [
  { key: 'program_material', label: 'Program Material' },
  { key: 'consumables', label: 'Consumables' },
  { key: 'petty_cash', label: 'Petty Cash' },
  { key: 'repair_maintenance', label: 'Repair & Maintenance' }
]

// Canonical centre display order (HQ-defined sequence, NOT alphabetical) — used
// everywhere procurement lists centres (HQ dashboard, budgets, every centre
// picker) so the order is identical across the whole section. Any centre not in
// this list (e.g. "MG Road Test centre", or a newly added centre) sorts to the
// END, alphabetically. Matching is case-insensitive and trim-tolerant.
export const CENTRE_ORDER = [
  'Indiranagar',
  'Jayanagar',
  'JP Nagar',
  'Sadashivnagar',
  'Salt Lake',
  'Sarjapur',
  'Theatre Road',
  'Whitefield',
  'HRBR Layout',
  'Sahakar Nagar',
  'Haralur',
  'HSR Layout'
]

const centreRank = (name) => {
  const key = String(name || '').trim().toLowerCase()
  const i = CENTRE_ORDER.findIndex((n) => n.toLowerCase() === key)
  return i === -1 ? CENTRE_ORDER.length : i
}

// Order a list of centre-like objects ({ name }) by CENTRE_ORDER; unknowns last
// (alphabetical tie-break). Returns a NEW array; safe on null/undefined input.
export const orderCentres = (list = []) =>
  [...(list || [])].sort((a, b) => {
    const ra = centreRank(a && a.name)
    const rb = centreRank(b && b.name)
    if (ra !== rb) return ra - rb
    return String((a && a.name) || '').localeCompare(String((b && b.name) || ''))
  })

// Starter SOP text per category — mirrors the backend seed (migration 0019).
// Used by the offline mock and as a fallback if the API has no row for a
// category. The live text comes from the API (HQ edits it in the SOP tab).
export const DEFAULT_SOPS = {
  program_material:
    '1. Search the catalogue and add each item you need this month, then set its quantity.\n' +
    "2. Make sure every line has a price — the catalogue fills it in; type it where it's blank.\n" +
    '3. Add a reference link or photo on every line so HQ knows exactly what to buy.\n' +
    "4. Keep the month's total within your centre's remaining budget across all categories.\n" +
    '5. Use Save draft to keep working — nothing reaches HQ until you Submit.\n' +
    '6. When the list is final, click Submit to send the order to HQ for approval.\n' +
    "7. Track HQ's decision under Notifications, and fix and resubmit anything sent back with remarks.\n" +
    '8. A submitted order is View-only until HQ responds; if HQ sends it back, it becomes editable again to fix and resubmit.',
  consumables:
    '1. Go down the consumables list and enter a quantity only for what you need — leave the rest at 0.\n' +
    '2. Check the unit price on each line you fill in.\n' +
    '3. Tick "Review your order" (top toolbar) to show only the items you have added, then check them over.\n' +
    '4. Use Save draft to keep working; submitting locks the month, so review the full list first.\n' +
    "5. Click Submit to send the month's consumables to HQ for approval.\n" +
    '6. A single category may go over its budget (adjusted against your other categories) — keep the overall centre budget in mind.\n' +
    "7. After submitting, watch Notifications for HQ's decision; ask HQ to reopen the month to make changes.",
  petty_cash:
    '1. Add one row per spend with its category and amount.\n' +
    '2. Save draft keeps your rows on this device without a bill — come back and finish later.\n' +
    '3. Nothing counts against the budget until you Submit.\n' +
    '4. Attach or photograph the supporting bill for every row — 📎 Invoice to upload, or 📷 Photo to shoot it on your phone. A bill is required to submit.\n' +
    '5. Click Submit to record the spends against the petty-cash budget.\n' +
    '6. The month locks automatically on the 3rd of next month; ask HQ to unlock if you need to edit after that.',
  repair_maintenance:
    '1. Add one row per repair or maintenance spend with its category and amount.\n' +
    '2. Save draft keeps your rows on this device without a bill — come back and finish later.\n' +
    '3. Nothing counts against the budget until you Submit.\n' +
    '4. Attach or photograph the supporting bill for every row — 📎 Invoice to upload, or 📷 Photo to shoot it on your phone. A bill is required to submit.\n' +
    '5. Click Submit to record the spends against the repair & maintenance budget.\n' +
    '6. The month locks automatically on the 3rd of next month; ask HQ to unlock if you need to edit after that.',
  dashboard:
    "1. Your monthly budget is set per category — Program Material, Consumables, Petty Cash and Repair & Maintenance. The four together are your centre's budget for the month.\n" +
    '2. Centre Budget is the summary that governs: your budget, what you have spent, and what is left to spend. Each category card shows what that category was allowed, what it spent, and whether it is spare or over — and the four cards add up to the amount left.\n' +
    "3. Program Material, Consumables and Repair & Maintenance are soft caps. You can go over one of them for a genuine need as long as your centre's total still holds; the card will say the overspend is covered by the centre's spare.\n" +
    '4. Petty Cash is a hard cap. You cannot record a spend that takes petty cash past its budget for the month, so check what is left before you spend the cash — if you need more, ask HQ to move budget into petty cash first.\n' +
    "5. Your centre's total across all four categories is also a hard cap, so a large order in one category leaves less for the others.\n" +
    '6. Months freeze after the cutoff (petty cash and R&M lock on the 3rd of next month); ask HQ to unlock if you must edit.\n' +
    '7. If you expect to exceed a budget for a real need, add a remark when you submit so HQ has the context.',
  hq_budgets:
    "1. Set each centre's monthly cap per category in the Budgets matrix, then Save to apply.\n" +
    '2. The Dashboard shows live usage (used vs budget) per centre and category for the selected month.\n' +
    '3. Use Transfer to move budget between centres or categories when one needs more and another has spare.\n' +
    '4. Budgets are a soft cap — centres can overspend; watch the Dashboard for centres trending over.\n' +
    '5. Use the Dashboard overrides to unlock petty cash or reopen consumables for a centre when needed.\n' +
    '6. Set budgets early each month so centres see their limits before they order.\n' +
    '7. Program Material budgets release the monthly cycle: nothing is sent to any centre until ALL 10 Bangalore centres have a Program Material budget for that month. Enter nine and nothing goes out — this is deliberate, not a fault.\n' +
    '8. The centre messages go out the morning AFTER the last budget lands, not immediately, so there is time to spot a wrong figure.\n' +
    '9. Once the centres have been messaged, editing or deleting a budget does NOT stop the cycle or re-send anything — that month has already started.\n' +
    '10. To see exactly what is about to be sent, to whom and in which channel, open HQ → Gordon → "What\'s next". Check it there before assuming a message did or did not go.',
  // Condensed from the Finance SOP doc, section 'Cash Fee Collection at
  // Centres'. Fallback only — the live text is the ProcurementSop row seeded
  // by oh_procurement migration 0073 and editable from HQ -> SOP.
  cash_register:
    'Cash is not our preferred payment mode. Accept it only when a parent insists and refusing would hurt fee collection or the parent\'s experience — always offer the Razorpay link, UPI or a bank transfer first. You are allowed to say no.\n' +
    '\n' +
    'WHO IS RESPONSIBLE\n' +
    'The Admissions Manager is the custodian of all cash collected at the centre. Finance verifies the records, reconciles them against the bank, and may audit or spot-check at any time.\n' +
    '\n' +
    'HOW A COLLECTION REACHES THIS PAGE\n' +
    '1. Collect the cash and record it the SAME DAY through Orders → Collect, with payment mode "Cash". An unrecorded collection is a serious SOP violation.\n' +
    '2. It appears here automatically. Check Received against Booked, and correct Received if the cash that actually came in was different — a changed plan, a discount, part of the fee paid by UPI or card, wallet credit adjusted, or change handed back. Pick the reason: that is what tells Finance the gap is explained rather than missing.\n' +
    '3. Keep the cash in a locked cash box or safe with restricted access. If your centre has no locker or cash box, raise a request — Finance will arrange one and the cost sits with the centre budget. Until then bank the cash at the earliest and never leave it unattended.\n' +
    '4. When you bank it or hand it over, tick those collections and use Mark settled — the bank and challan for a deposit, or the person\'s name for a handover.\n' +
    '\n' +
    'DEPOSIT RULES\n' +
    '• Deposit every week without fail, whatever the amount. A small balance is not a reason to wait.\n' +
    '• If cash at the centre crosses ₹1,00,000 at the end of any day, bank it within 24 hours.\n' +
    '• Deposit only into a designated company account below. Depositing into anyone\'s personal account is strictly prohibited.\n' +
    '• Share your weekly deposit confirmation with Finance on the centre Slack group.\n' +
    '• Never spend collected cash — no centre expenses, petty cash, reimbursements, vendor payments or salary advances — unless Finance approves it in writing.\n' +
    '• Report any mismatch between cash collected, recorded and deposited to Finance immediately.\n' +
    '• If a deposit will be delayed (short staff, weekly off), tell Finance, get approval, and deposit by the agreed date. Every safety and responsibility rule still applies meanwhile.\n' +
    '• Cash may be handed to Finance at HQ instead of a bank, as long as it is within the weekly deadline and recorded here. Handing cash to a visiting colleague needs Finance\'s prior approval, and it stays your responsibility until Finance confirms receipt.\n' +
    '\n' +
    'PART CASH, PART ONLINE\n' +
    'Allowed. Record the cash portion here with mode Cash, set Received to what actually came in as notes, pick "Part paid by UPI / QR / POS / bank" as the reason, and flag the split to Finance on your centre Slack group.\n' +
    '\n' +
    'DESIGNATED BANK ACCOUNTS (all: OPENHOUSE TECHNOLOGIES PRIVATE LIMITED)\n' +
    'HDFC Bank — 50200046242256 — HDFC0004395 — Maddox Square\n' +
    'Kotak Mahindra Bank — 7512142150 — KKBK0000322 — Kolkata, Park Street\n' +
    'RBL Bank — 409001639156 — RATN0000345 — Golpark\n' +
    '\n' +
    'Any shortage, loss, unrecorded collection, delayed deposit or unexplained variance is the Admissions Manager\'s responsibility, and Finance may recover the amount after written communication and a review of the records. Repeated violations may lead to disciplinary action. Zero tolerance for discrepancies, delays or unauthorised handling of cash.'
}

// All SOP sections in editor order: the 4 centre category SOPs + 2 budgeting
// SOPs. Drives the HQ SOP editor and the (i) dialog title lookup.
export const SOP_SECTIONS = [
  ...PROCUREMENT_CATEGORIES,
  { key: 'dashboard', label: 'Dashboard (budgeting)' },
  { key: 'hq_budgets', label: 'HQ Budgets' },
  // Not a procurement page — the Cash Register borrows this table so Finance
  // can edit its SOP from the same HQ -> SOP tab as everything else.
  { key: 'cash_register', label: 'Cash Register' }
]

export const PETTY_CASH_ENTRY_TYPES = [
  { key: 'received', label: 'Received' },
  { key: 'spent', label: 'Spent' }
]

// Petty-cash spend categories (value == label; mirrors the backend enum).
export const PETTY_CASH_CATEGORIES = [
  'Fruits for preschool',
  'Snacks for preschool',
  'Delivery charges - preschool',
  'Delivery charges - others',
  'Water purifier recharge',
  'BBMP - Garbage disposal charges',
  'General centre expense'
]

// Repair & Maintenance spend categories (value == label). Starter list — add
// new heads here as they come up; no other code change is needed. Keep in sync
// with the oh-api RepairMaintenanceCategory enum.
export const REPAIR_MAINTENANCE_CATEGORIES = [
  'Electrical repairs',
  'Plumbing & sanitary',
  'Carpentry & furniture repair',
  'Painting & civil work',
  'AC & appliance servicing',
  'Pest control',
  'Equipment & fixtures maintenance',
  'General repairs & maintenance'
]

// R&M "Paid by" (Satya, 4 Nov 2026) — mirrors oh-api PettyCashPaidBy. Whoever
// paid, the line counts against the centre's R&M budget.
export const RM_PAID_BY_OPTIONS = [
  { value: 'centre', label: 'Centre' },
  { value: 'azam', label: 'Azam' },
  { value: 'finance_team', label: 'Finance team' }
]
export const rmPaidByLabel = (v) =>
  (RM_PAID_BY_OPTIONS.find((o) => o.value === v) || {}).label || '—'

// R&M approval (outside budget) lines: where the approval was given (Satya,
// 5 Nov 2026) — mirrors oh-api RmApprovalSource.
export const RM_APPROVAL_SOURCE_OPTIONS = [
  { value: 'mail', label: 'Mail' },
  { value: 'slack', label: 'Slack' }
]
export const rmApprovalSourceLabel = (v) =>
  (RM_APPROVAL_SOURCE_OPTIONS.find((o) => o.value === v) || {}).label || '—'
